import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'

/**
 * Check if a facet slug is from the CategoriesSelector (should be preserved on homepage).
 *
 * CategoriesSelector facets include:
 * - BYO subcategory facets: tent, roof-tent, trailer-tent, touring-caravan, motorhome, campervan
 * - Electric facet: the electric hookup toggle in CategoriesSelector
 *
 * NOT included:
 * - Onsite accommodation subcategories (bell-tents, yurts, cabins, etc.)
 * - Other facility/feature facets (dogs-allowed, wifi, hot-tub, etc.)
 *
 * @param facetSlug - The facet slug to check (e.g., 'tent', 'electric')
 * @param language - Language code (e.g., 'en-gb')
 * @returns true if the facet is from CategoriesSelector, false otherwise
 */
export function isPitchTypeFacet(facetSlug: string, language: string): boolean {
  const presenter = getCampsiteCategoriesPresenter(language)
  return presenter.categorySelectorFacetSlugs.includes(facetSlug)
}

/**
 * Filter a facet value to only include category pitch type facets.
 *
 * This is used when navigating to/from homepage to preserve only pitch type
 * selections (what accommodation the user is bringing/renting), while excluding
 * facility/feature filters like electric hookups or dog-friendliness.
 *
 * @param facets - Single facet string, array of facets, or undefined
 * @param language - Language code (e.g., 'en-gb')
 * @returns Filtered facets in same format as input, or undefined if none remain
 *
 * @example
 * // Single pitch type facet - preserved
 * filterPitchTypeFacets('tent', 'en-gb') // => 'tent'
 *
 * @example
 * // Single non-pitch-type facet - removed
 * filterPitchTypeFacets('electric', 'en-gb') // => undefined
 *
 * @example
 * // Mixed facets - only pitch types preserved
 * filterPitchTypeFacets(['tent', 'electric', 'motorhome'], 'en-gb')
 * // => ['tent', 'motorhome']
 *
 * @example
 * // Multiple pitch types - both preserved as array
 * filterPitchTypeFacets(['tent', 'motorhome'], 'en-gb')
 * // => ['tent', 'motorhome']
 *
 * @example
 * // Only one pitch type remains - returned as string
 * filterPitchTypeFacets(['tent', 'wifi'], 'en-gb') // => 'tent'
 */
export function filterPitchTypeFacets(
  facets: string | string[] | undefined,
  language: string,
): string | string[] | undefined {
  if (!facets) return undefined

  const facetArray = Array.isArray(facets) ? facets : [facets]
  const filtered = facetArray.filter((f) => isPitchTypeFacet(f, language))

  if (filtered.length === 0) return undefined
  if (filtered.length === 1) return filtered[0]
  return filtered
}
